import { createContext, useContext, useEffect, useState } from 'react'
import { useResErrors, useRoutes, useStateCallback } from 'hooks'
import { SalesService, TaskService } from 'lib/api'
import { useParams } from 'react-router'
import { CS_CALL_CLIENT, CS_MEET_CLIENT } from 'constants/events'
import FinbrokerService from 'lib/api/services/finbroker.service'
import BeeService from 'lib/api/services/bee.service'
import CsContractsService from 'lib/api/services/csContracts.service'
import { useWorkListStoreManager } from 'context/HomeStore/Manager/WorkListsProvider/WorkListProvider.manager'
import OktellService from 'lib/api/services/oktell.service'

export const WorkListViewStoreContext = createContext({
  workListViewStore: {},
  workListViewStoreFunc: {}
})

export const WorkListViewStoreProviderMn = props => {
  const { standardCheck } = useResErrors()
  const { goPrevPage } = useRoutes()
  const { workListStore } = useWorkListStoreManager()
  const [sale, setSale] = useStateCallback({})
  const [initLoading, setInitLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useStateCallback(0)
  const [taskList, setTaskList] = useState([])
  const [creditList, setCreditList] = useState([])
  const [isClosedTasks, setIsClosedTasks] = useStateCallback(false)
  const [calls, setCalls] = useState([])
  const [wlAssessments, setWlAssessments] = useState([])
  const [comments, setComments] = useState([])
  const [contract, setContract] = useState(null)
  const [agreement, setAgreement] = useState(null)

  const { id: saleIdParam } = useParams()
  const saleId = props?.saleId || saleIdParam

  useEffect(() => {
    initLoad()
    return () => {}
  }, [])

  const initLoad = async () => {
    setInitLoading(true)

    await SalesService.getSaleByIdForView(saleId).then(response =>
      standardCheck(
        response,
        async result => {
          setSale(result.data)
          await getTaskList(Number(saleId))()
          await getContract(result.data?.globalId)()
          await getAgreement(result.data?.globalId)()
        },
        {
          onFail: () => goPrevPage(),
          textErr: 'Sale not found'
        }
      )
    )

    setInitLoading(false)
  }

  const getTaskList =
    (dealId = Number(saleId), completed = isClosedTasks) =>
    async () => {
      setLoading(true)
      await TaskService.getMyEvents({
        dealId,
        completed,
        types: [CS_CALL_CLIENT, CS_MEET_CLIENT]
      }).then(res =>
        standardCheck(res, async res => {
          setTaskList(res.data?.listEvents || [])
        })
      )
      setLoading(false)
    }

  const getCreditRequests =
    (globalId = sale?.globalId) =>
    async () => {
      setLoading(true)
      await FinbrokerService.getCreditRequests(globalId).then(res =>
        standardCheck(res, async res => {
          if (res.data.length > 0) {
            const data = res.data.map(item => {
              const keys = {
                approvedByMogo: 'Mogo',
                approvedByKmf: 'Kmf',
                approvedByRangeld: 'Rangeld',
                approvedByShinhan: 'Shinhan',
                approvedByBcc: 'Bcc',
                approvedByKaspi: 'Kaspi',
                approvedByMycar: 'Mycar',
                approvedByHalyk: 'Halyk',
                approvedByBereke: 'Bereke',
                approvedByTfs: 'Tfs',
                approvedByFreedom: 'Freedom',
                approvedByEurasianBank: 'EurasianBank',
                approvedByForte: 'Forte'
              }
              const keysAlternative = {
                bccIsAlternative: 'Bcc',
                berekeIsAlternative: 'Bereke',
                eurasianIsAlternative: 'EurasianBank',
                forteIsAlternative: 'Forte',
                freedomIsAlternative: 'Freedom',
                halykIsAlternative: 'Halyk',
                kaspiIsAlternative: 'Kaspi',
                kmfIsAlternative: 'Kmf',
                mogoIsAlternative: 'Mogo',
                mycarIsAlternative: 'Mycar',
                rangeldIsAlternative: 'Rangeld',
                sapaIsAlternative: 'Sapa',
                shinhanIsAlternative: 'Shinhan',
                tfsIsAlternative: 'Tfs'
              }
              const banksAlternative = Object.keys(keysAlternative)
                .filter(
                  key => item?.hasOwnProperty(key) && item?.[key] === true
                )
                .map(value => {
                  const decisionDescriptionKey = `${value.replace(
                    'IsAlternative',
                    'DecisionDescription'
                  )}`
                  return {
                    name: keysAlternative[value],
                    decisionDescription: item?.[decisionDescriptionKey]
                  }
                })

              const banks = Object.keys(keys)
                .filter(key => typeof item?.[key] === 'boolean')
                .map(value => {
                  const bankInfo = {
                    name: keys[value],
                    approved: item?.[value],
                    date: item?.createdAt,
                    creditAmount: item?.creditAmount,
                    downPaymentAmount: item?.downPaymentAmount,
                    creditTerm: item?.creditTerm,
                    saleGlobalId: item?.saleGlobalId,
                    vin: item?.carVinCode,
                    manager: item?.createdByFullName,
                    branch: workListStore.branches?.find(
                      br => br.code === item?.branchCode
                    )?.name
                  }

                  const alternativeInfo = banksAlternative.find(
                    alt => alt?.name === bankInfo?.name
                  )
                  if (!!alternativeInfo) {
                    bankInfo.decisionDescription =
                      alternativeInfo?.decisionDescription
                    bankInfo.alternative = true
                  }

                  return bankInfo
                })

              return {
                clientFirstname: item.clientFirstname,
                clientLastname: item.clientLastname,
                clientPatronymic: item.clientPatronymic,
                clientIIN: item.clientIIN,
                banks: banks
              }
            })
            return setCreditList(data || [])
          }
          setCreditList(res.data || [])
        })
      )
      setLoading(false)
    }

  const getContract =
    (globalId = sale?.globalId) =>
    async () => {
      await CsContractsService.getContract(globalId).then(res => {
        if (res.status === 200) {
          setContract(res.data || null)
        }
      })
    }
  const getAllComments =
    (globalId = sale?.globalId) =>
    async () => {
      await SalesService.getAllComments(globalId).then(res => {
        if (res.status === 200) {
          setComments(res?.data)
        }
      })
    }
  const getAgreement =
    (globalId = sale?.globalId) =>
    async () => {
      await CsContractsService.getAgreementByGlobalId(globalId).then(res => {
        if (res.status === 200) {
          setAgreement(res.data || null)
        }
      })
    }

  const getCallsList = async phone => {
    setLoading(true)
    const beeRes = await BeeService.getListByPhone(phone)
    standardCheck(
      beeRes,
      async () => {
        if (beeRes.data.length > 0) {
          const calls = await Promise.all(
            beeRes.data.map(async call => {
              const { callID, status, conversationDuration } = call
              const callLink =
                callID &&
                conversationDuration > 0
                  ? await BeeService.getCallFileByCallId(callID)
                  : null
              return { ...call, callLink: callLink?.data?.link || null }
            })
          )
          setCalls(prevState => [...prevState, ...calls])
        } else {
          setCalls([])
        }
      },
      {
        onFail: () => {
          setCalls([])
        }
      }
    )
    const oktellRes = await OktellService.getResultByPhone(phone)
    standardCheck(oktellRes, async () => {
      setCalls(prevState => [...prevState, ...(oktellRes.data || [])])
    })
    setLoading(false)
  }

  const getWlAssessments = async () => {
    await SalesService.getDealsByPhone(sale?.client?.phoneNumber).then(res =>
      standardCheck(res, async res => {
        setWlAssessments(res.data?.list || [])
      })
    )
  }

  const onChangeTab = (event, newValue) => {
    setSelectedTab(newValue, async s => {
      if (s === 1) {
        return await getAllComments(sale?.globalId)()
      }
      if (s === 2) {
        return await getCreditRequests(sale?.globalId)()
      }
      if (s === 3) {
        return (
          sale?.client?.phoneNumber &&
          (await getCallsList(sale?.client?.phoneNumber?.toStandardPhone()))
        )
      }
    })
  }

  const onSetIsClosedTasks = (bool = false) => {
    setIsClosedTasks(bool, () => {
      getTaskList(undefined, bool)()
    })
  }

  const addComments = async (comment, cb = () => {}) => {
    await SalesService.addSaleComments(sale.id, comment).then(res =>
      standardCheck(res, () => {
        cb()
        getAllComments(sale?.globalId)()
      })
    )
  }

  const workListViewStore = {
    contract,
    agreement,
    initLoading,
    loading,
    sale,
    selectedTab,
    taskList,
    isClosedTasks,
    calls,
    creditList,
    wlAssessments,
    comments
  }

  const workListViewStoreFunc = {
    onChangeTab,
    onSetIsClosedTasks,
    getWlAssessments,
    addComments,
    getTaskList: getTaskList()
  }

  return (
    <WorkListViewStoreContext.Provider
      value={{ workListViewStore, workListViewStoreFunc }}
    >
      {props.children}
    </WorkListViewStoreContext.Provider>
  )
}

export default WorkListViewStoreProviderMn

export const useWorkListViewMnStore = () => useContext(WorkListViewStoreContext)

export const withWorkListViewStore = Component => props =>
  (
    <WorkListViewStoreContext.Consumer>
      {state => <Component {...props} {...state} />}
    </WorkListViewStoreContext.Consumer>
  )
